import React from "react";
import PropTypes from "prop-types";
import { Button, Plus, Select, XClose } from "@opendate-app/setlist";
import { blankPricePoint, DAY_OF_SHOW_NAME } from "../scheduleBuilder";
import ShowDetailOffset, { MAX_OFFSET_MINUTES } from "./ShowDetailOffset";

const DAY_OF_SHOW_MOMENTS = ["Doors|0", "Event Start|0"];

const momentsOf = (point) =>
  point.showActivityName ? [`${point.showActivityName}|${Number(point.offsetMinutes) || 0}`] : DAY_OF_SHOW_MOMENTS;

export const pricePointProblems = (points) =>
  Object.fromEntries(
    points.map((point, index) => {
      const earlier = points.slice(0, index).flatMap(momentsOf);
      let problem = null;
      if (point.price === "" || point.price == null) problem = "Set a price.";
      else if (Math.abs(Number(point.offsetMinutes) || 0) > MAX_OFFSET_MINUTES) problem = "Start within a year of the show detail.";
      else if (momentsOf(point).some((moment) => earlier.includes(moment)))
        problem = "Another price change starts at the same time.";
      return [point.id, problem];
    }),
  );

const PriceChangesSection = ({ pricePoints, showActivityNames, problems, onChange }) => {
  const anchorOptions = [
    { value: "", label: `${DAY_OF_SHOW_NAME} (doors, or the show's start)` },
    ...showActivityNames.map((name) => ({ value: name, label: name })),
  ];
  const update = (id, fields) => onChange(pricePoints.map((point) => (point.id === id ? { ...point, ...fields } : point)));

  return (
    <>
      <h3 className="series-drawer-section-title">Price changes</h3>
      <p className="series-edit-hint">Change the price on every date at a time set by that date's show details.</p>
      {pricePoints.map((point, index) => (
        <div key={point.id} className="series-plan-subrow" data-testid="series-price-change">
          <div className="series-plan-subrow-header">
            <input
              aria-label={`Price change ${index + 1} name`}
              className="series-edit-input"
              placeholder="Advanced, Day of Show, etc..."
              value={point.name}
              onChange={(event) => update(point.id, { name: event.target.value })}
            />
            <Button
              variant="tertiary"
              size="sm"
              icon={XClose}
              aria-label={`Remove price change ${index + 1}`}
              onClick={() => onChange(pricePoints.filter((candidate) => candidate.id !== point.id))}
            />
          </div>
          <div className="series-edit-row">
            <label className="series-edit-label">
              Price
              <input
                type="number"
                min="0"
                step="0.01"
                className="series-edit-input"
                value={point.price}
                onChange={(event) => update(point.id, { price: event.target.value })}
              />
            </label>
            <Select
              label="Starts at"
              options={anchorOptions}
              value={point.showActivityName || ""}
              onChange={(value) => update(point.id, { showActivityName: value || "", offsetMinutes: 0 })}
            />
          </div>
          {point.showActivityName && (
            <ShowDetailOffset
              label={`Price change ${index + 1}`}
              anchorName={point.showActivityName}
              minutes={point.offsetMinutes}
              onChange={(minutes) => update(point.id, { offsetMinutes: minutes })}
            />
          )}
          {problems[point.id] && <p className="series-schedule-error">{problems[point.id]}</p>}
        </div>
      ))}
      <Button variant="link-color" icon={Plus} onClick={() => onChange([...pricePoints, blankPricePoint()])}>
        Add a price change
      </Button>
    </>
  );
};

PriceChangesSection.propTypes = {
  pricePoints: PropTypes.arrayOf(PropTypes.object).isRequired,
  showActivityNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  problems: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PriceChangesSection;
